/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Debug a sync run", description = "Find a sync run, read its outcome, inspect rejected rows down to the request and response payload, then fix and verify.", lastUpdatedDate = new Date(1787184000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Debug a sync run",
        description: "Find a sync run, read its outcome, inspect rejected rows down to the request and response payload, then fix and verify.",
        lastUpdatedDate: "Thu Aug 20 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Find the run",
          "depth": 0,
          "id": "find-the-run"
        }, {
          "value": "View run details",
          "depth": 0,
          "id": "view-run-details"
        }, {
          "value": "Inspect rejected rows",
          "depth": 0,
          "id": "inspect-rejected-rows"
        }, {
          "value": "Use the live debugger",
          "depth": 0,
          "id": "use-the-live-debugger"
        }, {
          "value": "Payload inspection",
          "depth": 1,
          "id": "payload-inspection"
        }, {
          "value": "Fix and verify",
          "depth": 0,
          "id": "fix-and-verify"
        }, {
          "value": "HTTP status codes",
          "depth": 0,
          "id": "http-status-codes"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      img: "img",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The live debugger shows every request and response Hightouch exchanged with your destination during a sync run, down to a single row."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams investigating a failed run or rejected rows"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: "A sync that has run at least once. Viewing row-level tabs requires debug access to the sync."
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "Use it to answer one question: did the run send the changes you expected, and if not, why. Work through it in order — find the run, read its outcome, isolate the rejected rows, inspect the payload, then fix and verify."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch retains row-level debugger logs for seven days, and you can't export\nrows from runs older than that. Run summaries stay available for 30 days. For\nlonger history, use ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), "."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "find-the-run",
        children: "Find the run"
      }), "\n", _jsxs(_components.p, {
        children: ["Open the ", _jsx(_components.strong, {
          children: "Runs"
        }), " tab on the sync's page. Each row is one run, with its ", _jsx(_components.strong, {
          children: "Started"
        }), " time, ", _jsx(_components.strong, {
          children: "Run status"
        }), ", ", _jsx(_components.strong, {
          children: "Rows queried"
        }), ", ", _jsx(_components.strong, {
          children: "Operations"
        }), ", and ", _jsx(_components.strong, {
          children: "Results"
        }), " (successful operations in green, rejected in red)."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/debugger/sync-runs-braze.png",
          alt: "Sync runs list showing runs completed with errors"
        })
      }), "\n", _jsx(_components.p, {
        children: "A run's status tells you where to look next:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Completed"
          }), " — the run finished and every row landed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Completed with errors"
          }), " — the run finished, but the destination rejected some rows. Open it and go to the ", _jsx(_components.strong, {
            children: "Rejected"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Failed"
          }), " — an error stopped the whole run before it could send rows. Click ", _jsx(_components.strong, {
            children: "See error message"
          }), " on the row to read it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Canceled by user"
          }), " — someone stopped the run."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Querying"
          }), ", ", _jsx(_components.strong, {
            children: "Processing"
          }), ", or ", _jsx(_components.strong, {
            children: "Queued"
          }), " — the run is still in progress."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/debugger/sync-runs-salesforce-marketing-cloud.png",
          alt: "Runs list with a failed run and its See error message link"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["To narrow a long list, click ", _jsx(_components.strong, {
          children: "Filter"
        }), " and choose ", _jsx(_components.strong, {
          children: "Hide runs with 0 operations"
        }), " or ", _jsx(_components.strong, {
          children: "Hide runs with 0 successful operations"
        }), ". Run timestamps appear in your local timezone under ", _jsx(_components.strong, {
          children: "Started"
        }), ", so you can line them up with when you noticed the problem."]
      }), "\n", _jsx(_components.h2, {
        id: "view-run-details",
        children: "View run details"
      }), "\n", _jsxs(_components.p, {
        children: ["Click ", _jsx(_components.strong, {
          children: "View run"
        }), " to open the run. The ", _jsx(_components.strong, {
          children: "Summary"
        }), " tab breaks the run into phases so you can see how far it got and where rows dropped:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Preparing to sync"
          }), " — waiting in queue and initializing."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Querying source"
          }), " — executing the model query and detecting changes, shown as added (", _jsx(_components.code, {
            children: "+"
          }), "), changed (", _jsx(_components.code, {
            children: "△"
          }), "), and removed (", _jsx(_components.code, {
            children: "−"
          }), ") rows."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Syncing to destination"
          }), " — ", _jsx(_components.strong, {
            children: "Sending data"
          }), ", with a count of rows synced successfully and rows rejected."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Wrapping up"
          }), " — saving logs."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/debugger/run-summary.png",
          alt: "Run summary showing the querying source phase"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["A red ", _jsx(_components.strong, {
          children: "Sending data"
        }), " bar with rejected rows is the signal to open the ", _jsx(_components.strong, {
          children: "Rejected"
        }), " tab and inspect them."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/run-summary-detail.png",
          alt: "A run summary with its Summary, Successful, Rejected, and Configuration tabs, showing rows rejected while sending data"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The added, changed, and removed counts reflect your ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync mode"
        }), ":"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Added"
          }), " rows are new in the model's query results since the previous run."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Changed"
          }), " rows were in the previous results but now have updated values."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Removed"
          }), " rows are no longer in the query results."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["For example, an ", _jsxs(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: [_jsx(_components.strong, {
            children: "insert"
          }), " mode"]
        }), " sync only shows ", _jsx(_components.strong, {
          children: "added"
        }), " rows even when rows left the model, and an ", _jsx(_components.a, {
          href: "/destinations/http-request",
          children: "HTTP request destination"
        }), " that only triggers on ", _jsx(_components.a, {
          href: "/destinations/http-request#choose-request-triggers",
          children: _jsx(_components.strong, {
            children: "Rows changed"
          })
        }), " only shows ", _jsx(_components.strong, {
          children: "changed"
        }), " rows."]
      }), "\n", _jsxs(_components.p, {
        children: ["If the run failed during query execution with a warehouse SQL error, see ", _jsx(_components.a, {
          href: "/models/sql-compilation-errors",
          children: "resolve SQL compilation errors"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "inspect-rejected-rows",
        children: "Inspect rejected rows"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Successful"
        }), " and ", _jsx(_components.strong, {
          children: "Rejected"
        }), " tabs list the individual rows in the run. To find a specific one, search by the value of the model's ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), " — the search matches on the primary key column only."]
      }), "\n", _jsx(_components.p, {
        children: "Each rejected row shows the error the destination returned for it. Hightouch passes these through as the third-party API sent them, including HTTP status codes and any trace IDs."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/run-rejected-rows.png",
          alt: "The Rejected tab listing rows with their primary keys and error messages"
        })
      }), "\n", _jsx(_components.p, {
        children: "To work through many rows at once, export the run's rows or just its row errors as a CSV or JSON file, then search them in your editor. Exporting requires permission to update the workspace, and is available for runs from the last seven days."
      }), "\n", _jsx(_components.h2, {
        id: "use-the-live-debugger",
        children: "Use the live debugger"
      }), "\n", _jsx(_components.p, {
        children: "To see the exact request and response behind a row, click the row to open the live debugger. It shows the request and response payloads the row was part of, along with the other rows in the same batch."
      }), "\n", _jsxs(_components.p, {
        children: ["For example, a ", _jsx(_components.code, {
          children: "POST"
        }), " request that fails with a 400 error shows the destination's error in the response — such as an ", _jsx(_components.code, {
          children: "undefined"
        }), " object in the request body. Hightouch also shows the request URL: an ", _jsx(_components.code, {
          children: "undefined"
        }), " value in the URL means a required ID never populated, which points you back to the model's query results."]
      }), "\n", _jsx(_components.h3, {
        id: "payload-inspection",
        children: "Payload inspection"
      }), "\n", _jsxs(_components.p, {
        children: ["Click the expand icon in the payload viewer to open a payload full-screen, then click in the body and press ", _jsx(_components.code, {
          children: "⌘F"
        }), " / ", _jsx(_components.code, {
          children: "Ctrl+F"
        }), " to search it for a row ID, email address, or any other value. Click the copy icon to copy the payload to your clipboard. For large nested payloads, paste the JSON into a tree viewer to browse it as collapsible properties."]
      }), "\n", _jsx(_components.h2, {
        id: "fix-and-verify",
        children: "Fix and verify"
      }), "\n", _jsx(_components.p, {
        children: "Match the fix to what the payload and error told you:"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "What you saw"
            }), _jsx(_components.th, {
              children: "Where to fix it"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A warehouse SQL error during querying"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/models/sql-compilation-errors",
                children: "Resolve SQL compilation errors"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A value in the wrong format or type"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/models/data-types-casting",
                children: "Data types and casting"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "The wrong record updated, or a matching failure"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/record-matching",
                children: "Record matching"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "A missing or malformed field in the request"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/syncs/mapping-data",
                children: "Field mapping"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["A ", _jsx(_components.code, {
                children: "401"
              }), " or credentials error"]
            }), _jsx(_components.td, {
              children: "The destination's connection settings"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: ["A ", _jsx(_components.code, {
                children: "500"
              }), "-series or rate-limit error"]
            }), _jsxs(_components.td, {
              children: ["Wait for the destination to recover; Hightouch ", _jsx(_components.a, {
                href: "/syncs/retries",
                children: "retries"
              }), " automatically"]
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: "After you change the model or sync configuration, run the sync again and confirm the new run completes with zero rejected rows."
      }), "\n", _jsx(_components.h2, {
        id: "http-status-codes",
        children: "HTTP status codes"
      }), "\n", _jsx(_components.p, {
        children: "Any HTTP status code in an error comes from your destination, not from Hightouch. The codes fall into five groups:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "1xx"
          }), " — informational; the request was received."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "2xx"
          }), " — success; the request was accepted."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "3xx"
          }), " — redirection; further action is needed."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "4xx"
          }), " — client error; the request was invalid or can't be fulfilled."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "5xx"
          }), " — server error; the destination failed to fulfill the request."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The codes you'll see most often:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "200"
          }), " — the destination accepted the request."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "400"
          }), " — usually invalid data. Check your values against the destination's API expectations."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "401"
          }), " — a credentials problem. Confirm your destination credentials have the required permissions."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "500-series"
          }), " (500, 502, 504) — a problem inside the destination. Check its status page for outages; Hightouch ", _jsx(_components.a, {
            href: "/syncs/retries",
            children: "retries"
          }), " the sync."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://developer.mozilla.org/en-US/docs/Web/HTTP/Status",
          children: "Mozilla's HTTP status reference"
        }), " for the full list."]
      }), "\n", _jsx(_components.p, {
        children: "Because Hightouch returns destination errors verbatim, pasting an unfamiliar message into a search engine or the destination's developer community is often the fastest way to understand it."
      })]
    });
  }
}
export default MDXContent;
